<template>
  <Teleport to="body">
    <div v-if="modelValue" class="fixed inset-0 z-50 overflow-hidden flex items-center justify-center p-2 sm:p-4" role="dialog">
      <!-- Overlay -->
      <div class="absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity" @click="close"></div>

      <!-- Modal Content -->
      <div 
        :class="[
          'relative bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full flex flex-col overflow-hidden transform transition-all animate-slideUp',
          maxWidthClass
        ]"
      >
        <!-- Header -->
        <div class="p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700">
          <div class="flex justify-between items-center">
            <h3 class="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-3">
              <div v-if="icon" :class="['p-2 rounded-lg', iconBgClass]">
                <Icon :icon="icon" :class="['text-xl', iconColorClass]" />
              </div>
              {{ title }}
            </h3>
            <button @click="close" class="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl transition-colors">
              <Icon icon="mdi:close" class="text-2xl text-gray-500 dark:text-gray-400" />
            </button>
          </div>
        </div>
        
        <!-- Body -->
        <div class="p-4 sm:p-6 overflow-y-auto flex-1 custom-scrollbar max-h-[70vh]">
          <slot></slot>
        </div>
        
        <!-- Footer -->
        <div v-if="$slots.footer" class="p-4 sm:p-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
          <slot name="footer"></slot>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script>
import { Icon } from "@iconify/vue";

export default {
  name: 'BaseModal',
  components: { Icon },
  props: {
    modelValue: {
      type: Boolean,
      required: true
    },
    title: {
      type: String,
      default: ''
    },
    icon: {
      type: String,
      default: ''
    },
    iconType: {
      type: String,
      default: 'primary' // primary, success, warning, danger, info
    },
    maxWidth: {
      type: String,
      default: 'max-w-2xl'
    }
  },
  emits: ['update:modelValue', 'close'],
  computed: {
    maxWidthClass() {
      return this.maxWidth;
    },
    iconBgClass() {
      const types = {
        primary: 'bg-blue-100 dark:bg-blue-900/30',
        success: 'bg-green-100 dark:bg-green-900/30',
        warning: 'bg-orange-100 dark:bg-orange-900/30',
        danger: 'bg-red-100 dark:bg-red-900/30',
        info: 'bg-indigo-100 dark:bg-indigo-900/30'
      };
      return types[this.iconType] || types.primary;
    },
    iconColorClass() {
      const types = {
        primary: 'text-blue-600 dark:text-blue-400',
        success: 'text-green-600 dark:text-green-400',
        warning: 'text-orange-600 dark:text-orange-400',
        danger: 'text-red-600 dark:text-red-400',
        info: 'text-indigo-600 dark:text-indigo-400'
      };
      return types[this.iconType] || types.primary;
    }
  },
  methods: {
    close() {
      this.$emit('update:modelValue', false);
      this.$emit('close');
    }
  }
};
</script>

<style scoped>
.custom-scrollbar::-webkit-scrollbar {
  width: 6px;
}
.custom-scrollbar::-webkit-scrollbar-track {
  background: transparent;
}
.custom-scrollbar::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 10px;
}
.dark .custom-scrollbar::-webkit-scrollbar-thumb {
  background: #475569;
}
</style>
